import React from 'react';
import { motion } from 'motion/react';
import { Calendar, Layout, Clock, CheckCircle2 } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const SocialMediaCalendar = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Social Media Content Calendar - Plan Your Strategy"
        description="Organize your social media strategy with PlataPost's AI content calendar. Plan weeks or months in advance with drag-and-drop ease."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Calendar className="w-3.5 h-3.5" />
              <span>Full Visibility</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI Social Media <br /> <span className="text-indigo-500">Content Calendar.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Visualize your entire social strategy. Our calendar helps you plan, review, and organize content across all platforms with zero clutter.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Open Your Calendar
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 border border-white/5 shadow-2xl shadow-indigo-600/5 relative overflow-hidden group">
             <div className="absolute inset-0 bg-indigo-600/5 blur-[100px] pointer-events-none" />
             <div className="grid grid-cols-7 gap-3 relative z-10 transition-transform duration-700 group-hover:scale-[1.02]">
               {Array.from({ length: 28 }).map((_, i) => (
                 <div key={i} className="aspect-square bg-white/5 rounded-xl border border-white/10 flex items-center justify-center transition-all hover:bg-white/10 hover:border-indigo-500/30">
                    {i % 4 === 0 && (
                      <motion.div 
                        initial={{ scale: 0.8, opacity: 0.5 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                        className="w-2.5 h-2.5 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)]" 
                      />
                    )}
                 </div>
               ))}
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-24 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Plan Like a Pro</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Intuitive tools built for modern social media managers.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Layout, title: "Drag & Drop Ease", desc: "Easily move posts between dates to balance your content mix." },
              { icon: Clock, title: "Historical Tracking", desc: "Keep track of what you've posted in the past to avoid repetition." },
              { icon: CheckCircle2, title: "Multi-Platform View", desc: "See your Instagram, LinkedIn, and Facebook posts all in one place." }
            ].map((feature, i) => (
               <div key={i} className="p-12 glass-card rounded-[3rem] border border-white/5 hover:bg-white/5 transition-all group">
                  <div className="w-20 h-20 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-10 text-indigo-400 group-hover:scale-110 group-hover:rotate-3 transition-all shadow-xl shadow-indigo-600/5">
                    <feature.icon className="w-10 h-10" />
                  </div>
                  <h3 className="text-3xl font-black text-white mb-6 tracking-tight">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
               </div>
            ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
          <div className="absolute inset-0 bg-indigo-600/10 blur-[150px] pointer-events-none" />
          <h2 className="text-4xl lg:text-6xl font-black mb-10 text-white tracking-tighter leading-tight relative z-10">Stop Reacting. <span className="text-indigo-400">Start Planning.</span></h2>
          <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto font-medium relative z-10 leading-relaxed">
            A messy spreadsheet is not a strategy. Get a professional AI content calendar today.
          </p>
          <Link to="/dashboard" className="relative z-10 inline-flex px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
            Build My Calendar
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaCalendar;
